/**
 * useHomeSLOrbHomeOrbitSimLogic
 *
 * [shared]-entity logic hook.
 * Reads/writes the shared-entity store instead of a per-trait
 * reducer — see @almadar/ui/hooks/useSharedEntityStore.
 * Dispatch resolves transitions + effects fully client-side (no
 * server round trip).
 *
 * @generated from schema
 * @trait HomeSLOrbHomeOrbitSim
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSharedEntityStoreContext, useSharedEntitySnapshot, useEventBus, useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { emitCascadeEvent } from '@/hooks/useOrbitalBridge';
import type { EntityFieldWrite, EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import { mergeEntityFrame } from '@almadar/core';
import type { BusEvent } from '@almadar/core';
import type { EventResponse, HomeSLOrbitState, HomeSLOrbHomeOrbitSimEventPayloadMap } from '@app/shared';

export interface HomeSLOrbHomeOrbitSimConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  backgroundColor: string;
  bodies: { color: string; fx: number; fy: number; id: string; label: string; radius: number; vx: number; vy: number; x: number; y: number }[];
  gm: number;
  height: number;
  matchMode: string;
  showVelocity: boolean;
  substeps: number;
  traceLength: number;
  velocityScale: number;
  width: number;
}

const DEFAULT_HOME_SL_ORB_HOME_ORBIT_SIM_CONFIG = {
  backgroundColor: "",
  bodies: [{ color: "series-1", fx: 0, fy: 0, id: "sun", label: "", radius: 20, vx: 0, vy: 0, x: 300, y: 200 }, { color: "series-2", fx: 0, fy: 0, id: "planet-a", label: "", radius: 10, vx: 2.24, vy: 0, x: 300, y: 80 }, { color: "series-3", fx: 0, fy: 0, id: "planet-b", label: "", radius: 8, vx: 0, vy: 1.88, x: 470, y: 200 }],
  gm: 600,
  height: 400,
  matchMode: "",
  showVelocity: true,
  substeps: 8,
  traceLength: 90,
  velocityScale: 14,
  width: 600,
} as HomeSLOrbHomeOrbitSimConfig;

type HomeSLOrbHomeOrbitSimSlot = 'main';
export interface HomeSLOrbHomeOrbitSimLogicReturn {
  state: string;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: string | null;
  slotWriter: Partial<Record<HomeSLOrbHomeOrbitSimSlot, string | null>>;
  loading: boolean;
  pendingEvent: string | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeSLOrbitState>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeSLOrbHomeOrbitSimEventPayloadMap]: (payload?: HomeSLOrbHomeOrbitSimEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeSLOrbHomeOrbitSimConfig;
}

export function useHomeSLOrbHomeOrbitSimLogic(config?: Partial<HomeSLOrbHomeOrbitSimConfig>): HomeSLOrbHomeOrbitSimLogicReturn {
  const mergedConfig: HomeSLOrbHomeOrbitSimConfig = { ...DEFAULT_HOME_SL_ORB_HOME_ORBIT_SIM_CONFIG, ...(config ?? {}) };

  // [shared] entity store + coalesced tick driver
  const _sharedStore = useSharedEntityStoreContext();
  const _sharedEntityId = 'HomeSLOrbitState';
  _sharedStore.seed(_sharedEntityId, { 'active': false, 'running': false, 'steps': 0, 'elapsedMs': 0, 'done': false, 'speed': 1 });
  const _sharedSnapshot = useSharedEntitySnapshot(_sharedStore, _sharedEntityId);

  const fields = _sharedSnapshot as Partial<HomeSLOrbitState>;
  const data: Record<string, EntityRow[]> = { 'HomeSLOrbitState': [_sharedSnapshot] };

  // Local per-trait machine state — NOT the shared entity's fields,
  // which always live in the shared store (read fresh on every
  // dispatch, never cached here).
  const [machineState, setMachineState] = useState<string>('idle');
  const [lastPayload, setLastPayload] = useState<EventPayload | undefined>(undefined);
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [slotWriter, setSlotWriter] = useState<Partial<Record<HomeSLOrbHomeOrbitSimSlot, string | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;

  const eventBus = useEventBus();

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['HomeSLOrbitState'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT' && (true)) {
      // idle → idle (on INIT)
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bodies'] = [{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }];
        fields = { ...(fields ?? {}), 'bodies': _fields['bodies'] } as Record<string, EventPayloadValue | undefined>;
        _fields['trails'] = ([{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }]).map((b) => ({ id: getNestedValue(b, "id"), points: [{ x: getNestedValue(b, "x"), y: getNestedValue(b, "y") }], color: getNestedValue(b, "color") }));
        fields = { ...(fields ?? {}), 'trails': _fields['trails'] } as Record<string, EventPayloadValue | undefined>;
        {

        const primary = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }], (1));

        _fields['vectors'] = [{ body: getNestedValue(primary, "id"), dx: getNestedValue(primary, "vx"), dy: getNestedValue(primary, "vy"), scale: 14, color: "series-3" as const, label: "v" as const }];
        fields = { ...(fields ?? {}), 'vectors': _fields['vectors'] } as Record<string, EventPayloadValue | undefined>;
        }

        {

        const cx = (600 / 2);

        const cy = (400 / 2);

        const primary = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }], (1));

        const dist = Math.hypot(((+(getNestedValue(primary, "x") ?? 0) - +(cx ?? 0))), ((+(getNestedValue(primary, "y") ?? 0) - +(cy ?? 0))));

        const speed = Math.hypot(Number(getNestedValue(primary, "vx") ?? 0), Number(getNestedValue(primary, "vy") ?? 0));

        _fields['readouts'] = [{ label: "r" as const, value: ["", +((Number(dist ?? 0)).toFixed((1)))].join('') }, { label: "v" as const, value: ["", +((Number(speed ?? 0)).toFixed((2)))].join('') }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        }

        _fields['narration'] = "ready — press Step to advance the orbits";
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('ORBIT2D_UPDATED', _p, { trait: 'HomeSLOrbHomeOrbitSim' }); emitCascadeEvent(eventBus, 'HomeSL', 'HomeSLOrbHomeOrbitSim', { event: 'ORBIT2D_UPDATED', payload: _p, source: { orbital: 'HomeSL', trait: 'HomeSLOrbHomeOrbitSim' } }); })({ bodies: fields?.bodies, trails: fields?.trails, readouts: fields?.readouts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((!true && !(payloadObj?.mode === "")))) {
      // idle → idle (on MODE_SELECTED)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((payloadObj?.mode === ""))) {
      // idle → idle (on MODE_SELECTED)
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bodies'] = [{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }];
        fields = { ...(fields ?? {}), 'bodies': _fields['bodies'] } as Record<string, EventPayloadValue | undefined>;
        _fields['trails'] = ([{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }]).map((b) => ({ id: getNestedValue(b, "id"), points: [{ x: getNestedValue(b, "x"), y: getNestedValue(b, "y") }], color: getNestedValue(b, "color") }));
        fields = { ...(fields ?? {}), 'trails': _fields['trails'] } as Record<string, EventPayloadValue | undefined>;
        {

        const primary = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }], (1));

        _fields['vectors'] = [{ body: getNestedValue(primary, "id"), dx: getNestedValue(primary, "vx"), dy: getNestedValue(primary, "vy"), scale: 14, color: "series-3" as const, label: "v" as const }];
        fields = { ...(fields ?? {}), 'vectors': _fields['vectors'] } as Record<string, EventPayloadValue | undefined>;
        }

        {

        const cx = (600 / 2);

        const cy = (400 / 2);

        const primary = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }], (1));

        const dist = Math.hypot(((+(getNestedValue(primary, "x") ?? 0) - +(cx ?? 0))), ((+(getNestedValue(primary, "y") ?? 0) - +(cy ?? 0))));

        const speed = Math.hypot(Number(getNestedValue(primary, "vx") ?? 0), Number(getNestedValue(primary, "vy") ?? 0));

        _fields['readouts'] = [{ label: "r" as const, value: ["", +((Number(dist ?? 0)).toFixed((1)))].join('') }, { label: "v" as const, value: ["", +((Number(speed ?? 0)).toFixed((2)))].join('') }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        }

        _fields['narration'] = "ready — press Step to advance the orbits";
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('ORBIT2D_UPDATED', _p, { trait: 'HomeSLOrbHomeOrbitSim' }); emitCascadeEvent(eventBus, 'HomeSL', 'HomeSLOrbHomeOrbitSim', { event: 'ORBIT2D_UPDATED', payload: _p, source: { orbital: 'HomeSL', trait: 'HomeSLOrbHomeOrbitSim' } }); })({ bodies: fields?.bodies, trails: fields?.trails, readouts: fields?.readouts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_STEP' && (((fields?.active === true) && !(fields?.done === true)))) {
      // idle → idle (on LEARN_STEP)
        {

        const cx = (600 / 2);

        const cy = (400 / 2);

        const newBodies = (Array.from({ length: Math.ceil(8) }, (_, i) => i)).reduce<EventPayloadValue>((acc, s) => ((acc ?? []) as readonly EntityRow[]).map((b) => ((getNestedValue(b, "id") === getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }], (0)), "id")) ? b : (() => { const dx = (+(cx ?? 0) - +(getNestedValue(b, "x") ?? 0)); const dy = (+(cy ?? 0) - +(getNestedValue(b, "y") ?? 0)); const dist = Math.hypot(Number(dx ?? 0), Number(dy ?? 0)); const distSafe = Math.max(Number(dist ?? 0), (6)); const invCube = (600 / (+(distSafe ?? 0) * (+(distSafe ?? 0) * +(distSafe ?? 0)))); const ax = (+(invCube ?? 0) * +(dx ?? 0)); const ay = (+(invCube ?? 0) * +(dy ?? 0)); const nvx = (+(getNestedValue(b, "vx") ?? 0) + +(ax ?? 0)); const nvy = (+(getNestedValue(b, "vy") ?? 0) + +(ay ?? 0)); const nx = (+(getNestedValue(b, "x") ?? 0) + +(nvx ?? 0)); const ny = (+(getNestedValue(b, "y") ?? 0) + +(nvy ?? 0)); return Object.assign({}, b, { x: nx, y: ny, vx: nvx, vy: nvy }); })())), fields?.bodies);

        const newTrails = ((newBodies ?? []) as readonly EntityRow[]).map((b, index: number) => (() => { const prevPts = getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)(fields?.trails, Number(index ?? 0)), "points"); const appended = [...((prevPts as readonly EventPayloadValue[] | undefined) ?? []), { x: getNestedValue(b, "x"), y: getNestedValue(b, "y") }]; const n = ((appended ?? []) as { readonly length: number }).length; return { id: getNestedValue(b, "id"), points: ((+(n ?? 0) > 90) ? ((appended as readonly EventPayloadValue[] | undefined) ?? []).slice((+(n ?? 0) - 90)) : appended), color: getNestedValue(b, "color") }; })());

        const primary = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)(newBodies, (1));

        const dist = Math.hypot(((+(getNestedValue(primary, "x") ?? 0) - +(cx ?? 0))), ((+(getNestedValue(primary, "y") ?? 0) - +(cy ?? 0))));

        const speed = Math.hypot(Number(getNestedValue(primary, "vx") ?? 0), Number(getNestedValue(primary, "vy") ?? 0));

        _fields['bodies'] = newBodies;
        fields = { ...(fields ?? {}), 'bodies': _fields['bodies'] } as Record<string, EventPayloadValue | undefined>;
        _fields['trails'] = newTrails;
        fields = { ...(fields ?? {}), 'trails': _fields['trails'] } as Record<string, EventPayloadValue | undefined>;
        _fields['vectors'] = [{ body: getNestedValue(primary, "id"), dx: getNestedValue(primary, "vx"), dy: getNestedValue(primary, "vy"), scale: 14, color: "series-3" as const, label: "v" as const }];
        fields = { ...(fields ?? {}), 'vectors': _fields['vectors'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "r" as const, value: ["", +((Number(dist ?? 0)).toFixed((1)))].join('') }, { label: "v" as const, value: ["", +((Number(speed ?? 0)).toFixed((2)))].join('') }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = ["r=", [["", +((Number(dist ?? 0)).toFixed((1)))].join(''), ["px, v=", ["", +((Number(speed ?? 0)).toFixed((2)))].join('')].join('')].join('')].join('');
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('ORBIT2D_UPDATED', _p, { trait: 'HomeSLOrbHomeOrbitSim' }); emitCascadeEvent(eventBus, 'HomeSL', 'HomeSLOrbHomeOrbitSim', { event: 'ORBIT2D_UPDATED', payload: _p, source: { orbital: 'HomeSL', trait: 'HomeSLOrbHomeOrbitSim' } }); })({ bodies: fields?.bodies, trails: fields?.trails, readouts: fields?.readouts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_RESET' && ((fields?.active === true))) {
      // idle → idle (on LEARN_RESET)
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bodies'] = [{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }];
        fields = { ...(fields ?? {}), 'bodies': _fields['bodies'] } as Record<string, EventPayloadValue | undefined>;
        _fields['trails'] = ([{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }]).map((b) => ({ id: getNestedValue(b, "id"), points: [{ x: getNestedValue(b, "x"), y: getNestedValue(b, "y") }], color: getNestedValue(b, "color") }));
        fields = { ...(fields ?? {}), 'trails': _fields['trails'] } as Record<string, EventPayloadValue | undefined>;
        {

        const primary = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }], (1));

        _fields['vectors'] = [{ body: getNestedValue(primary, "id"), dx: getNestedValue(primary, "vx"), dy: getNestedValue(primary, "vy"), scale: 14, color: "series-3" as const, label: "v" as const }];
        fields = { ...(fields ?? {}), 'vectors': _fields['vectors'] } as Record<string, EventPayloadValue | undefined>;
        }

        {

        const cx = (600 / 2);

        const cy = (400 / 2);

        const primary = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ id: "sun" as const, x: 300, y: 200, radius: 20, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }, { id: "planet-a" as const, x: 300, y: 80, radius: 10, color: "series-2" as const, label: "" as const, vx: 2.24, vy: 0, fx: 0, fy: 0 }, { id: "planet-b" as const, x: 470, y: 200, radius: 8, color: "series-3" as const, label: "" as const, vx: 0, vy: 1.88, fx: 0, fy: 0 }], (1));

        const dist = Math.hypot(((+(getNestedValue(primary, "x") ?? 0) - +(cx ?? 0))), ((+(getNestedValue(primary, "y") ?? 0) - +(cy ?? 0))));

        const speed = Math.hypot(Number(getNestedValue(primary, "vx") ?? 0), Number(getNestedValue(primary, "vy") ?? 0));

        _fields['readouts'] = [{ label: "r" as const, value: ["", +((Number(dist ?? 0)).toFixed((1)))].join('') }, { label: "v" as const, value: ["", +((Number(speed ?? 0)).toFixed((2)))].join('') }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        }

        _fields['narration'] = "ready — press Step to advance the orbits";
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('ORBIT2D_UPDATED', _p, { trait: 'HomeSLOrbHomeOrbitSim' }); emitCascadeEvent(eventBus, 'HomeSL', 'HomeSLOrbHomeOrbitSim', { event: 'ORBIT2D_UPDATED', payload: _p, source: { orbital: 'HomeSL', trait: 'HomeSLOrbHomeOrbitSim' } }); })({ bodies: fields?.bodies, trails: fields?.trails, readouts: fields?.readouts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_UPDATED' && ((fields?.active === true))) {
      // idle → idle (on LEARN_UPDATED)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeSLOrbHomeOrbitSimSlot[]; clear: HomeSLOrbHomeOrbitSimSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeSLOrbHomeOrbitSimSlot[], clear: [] as HomeSLOrbHomeOrbitSimSlot[] };
          case 'LEARN_RESET': return { write: ['main'] as HomeSLOrbHomeOrbitSimSlot[], clear: [] as HomeSLOrbHomeOrbitSimSlot[] };
          case 'LEARN_STEP': return { write: ['main'] as HomeSLOrbHomeOrbitSimSlot[], clear: [] as HomeSLOrbHomeOrbitSimSlot[] };
          case 'LEARN_UPDATED': return { write: ['main'] as HomeSLOrbHomeOrbitSimSlot[], clear: [] as HomeSLOrbHomeOrbitSimSlot[] };
          case 'MODE_SELECTED': return { write: ['main'] as HomeSLOrbHomeOrbitSimSlot[], clear: ['main'] as HomeSLOrbHomeOrbitSimSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event against the shared entity, committing locally
   * first (the fields mirror IS the `[runtime]` row).
   * No server round trip — this trait declares `local`.
   */
  const dispatch = useCallback(async (event: string, payload?: EventPayload): Promise<EventResponse> => {
    const prevState = machineStateRef.current;
    const liveData: Record<string, EntityRow[]> = { 'HomeSLOrbitState': [_sharedStore.getSnapshot(_sharedEntityId)] };
    const result = executeTransitionEffects(prevState, event, liveData, payload);
    if (result.newState === undefined) {
      const errorMsg = `No transition defined for ${event} from ${prevState}`;
      setError(errorMsg);
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const writes: EntityFieldWrite[] = Object.entries(result.fields)
      .filter(([, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value: value as FieldValue }));
    if (writes.length > 0) {
      _sharedStore.commit(_sharedEntityId, mergeEntityFrame(_sharedStore.getSnapshot(_sharedEntityId), writes));
    }
    for (const _emit of result.emits) _emit();
    machineStateRef.current = result.newState;
    setMachineState(result.newState);
    const _renderSlots = getRenderUiSlots(prevState, event);
    if (_renderSlots.write.length > 0 || _renderSlots.clear.length > 0) {
      setLastPayload(payload);
      setLastEvent(event);
      setSlotWriter((prev) => ({
        ...prev,
        ...Object.fromEntries(_renderSlots.write.map((s) => [s, event] as const)),
        ...Object.fromEntries(_renderSlots.clear.map((s) => [s, null] as const)),
      }));
    }
    setError(null);
    return { success: true, transitioned: true, states: { 'HomeSLOrbHomeOrbitSim': result.newState }, emittedEvents: [] } as EventResponse;
  }, [executeTransitionEffects, getRenderUiSlots, _sharedStore, _sharedEntityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeSLOrbHomeOrbitSimEventPayloadMap]: (payload?: HomeSLOrbHomeOrbitSimEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'MODE_SELECTED': (payload?: HomeSLOrbHomeOrbitSimEventPayloadMap['MODE_SELECTED']) => dispatch('MODE_SELECTED', payload),
    'LEARN_STEP': (payload?: HomeSLOrbHomeOrbitSimEventPayloadMap['LEARN_STEP']) => dispatch('LEARN_STEP', payload),
    'LEARN_RESET': () => dispatch('LEARN_RESET'),
    'LEARN_UPDATED': (payload?: HomeSLOrbHomeOrbitSimEventPayloadMap['LEARN_UPDATED']) => dispatch('LEARN_UPDATED', payload),
    'ORBIT2D_UPDATED': (payload?: HomeSLOrbHomeOrbitSimEventPayloadMap['ORBIT2D_UPDATED']) => dispatch('ORBIT2D_UPDATED', payload),
    'RESET': () => dispatch('RESET'),
    'STEP': () => dispatch('STEP'),
    'TOGGLE_RUN': () => dispatch('TOGGLE_RUN'),
  };

  useUIEvents(enqueueEvent, 'HomeSL.HomeSLOrbHomeOrbitSim', ['INIT', 'MODE_SELECTED', 'LEARN_STEP', 'LEARN_RESET', 'LEARN_UPDATED'] as const, eventBus);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'LEARN_RESET' (from HomeSLOrbHomeOrbitClock) → trigger 'LEARN_RESET'
    const unsub_learn_reset_from_homeslorbhomeorbitclock_to_learn_reset = eventBus.on('UI:HomeSL.HomeSLOrbHomeOrbitClock.LEARN_RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeSL.HomeSLOrbHomeOrbitSim', 'LEARN_RESET')) return;
      enqueueEvent('LEARN_RESET', event.payload);
    });
    // Listen for 'LEARN_STEP' (from HomeSLOrbHomeOrbitClock) → trigger 'LEARN_STEP'
    const unsub_learn_step_from_homeslorbhomeorbitclock_to_learn_step = eventBus.on('UI:HomeSL.HomeSLOrbHomeOrbitClock.LEARN_STEP', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeSL.HomeSLOrbHomeOrbitSim', 'LEARN_STEP')) return;
      enqueueEvent('LEARN_STEP', event.payload);
    });
    // Listen for 'LEARN_UPDATED' (from HomeSLOrbHomeOrbitClock) → trigger 'LEARN_UPDATED'
    const unsub_learn_updated_from_homeslorbhomeorbitclock_to_learn_updated = eventBus.on('UI:HomeSL.HomeSLOrbHomeOrbitClock.LEARN_UPDATED', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeSL.HomeSLOrbHomeOrbitSim', 'LEARN_UPDATED')) return;
      enqueueEvent('LEARN_UPDATED', event.payload);
    });
    return () => {
      if (typeof unsub_learn_reset_from_homeslorbhomeorbitclock_to_learn_reset === 'function') unsub_learn_reset_from_homeslorbhomeorbitclock_to_learn_reset();
      if (typeof unsub_learn_step_from_homeslorbhomeorbitclock_to_learn_step === 'function') unsub_learn_step_from_homeslorbhomeorbitclock_to_learn_step();
      if (typeof unsub_learn_updated_from_homeslorbhomeorbitclock_to_learn_updated === 'function') unsub_learn_updated_from_homeslorbhomeorbitclock_to_learn_updated();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state: machineState,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading: false,
    pendingEvent: null,
    pendingFrom: null,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
